import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { CalendarSync, Loader2, Link as LinkIcon, X, ExternalLink } from 'lucide-react';

export const NeedsLinking = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [dialog, setDialog] = useState({ open: false, event: null, customerId: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [r, cr] = await Promise.all([
        api.get('/calendar-sync/unlinked'),
        api.get('/customers?limit=500'),
      ]);
      setEvents(r.data || []);
      const arr = Array.isArray(cr.data) ? cr.data : (cr.data.customers || []);
      setCustomers(arr);
    } catch {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/calendar-sync/hello');
      toast.success(`Synced ${r.data.synced} events · ${r.data.auto_matched} auto-matched · ${r.data.unlinked} need linking`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const link = async () => {
    if (!dialog.customerId) return;
    try {
      await api.post(`/calendar-sync/unlinked/${dialog.event.id}/link`, { customer_id: dialog.customerId });
      toast.success('Event linked to customer');
      setDialog({ open: false, event: null, customerId: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Link failed');
    }
  };

  const dismiss = async (ev) => {
    if (!window.confirm('Dismiss this event (permanently removes from the list)?')) return;
    try {
      await api.delete(`/calendar-sync/unlinked/${ev.id}`);
      load();
    } catch {
      toast.error('Failed');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="needs-linking-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">
              <CalendarSync className="inline w-7 h-7 mr-2 text-blue-600" />
              Needs Linking
            </h1>
            <p className="text-slate-500 mt-1">Outlook events from hello@helioses.co.uk that couldn't be auto-matched to a customer.</p>
          </div>
          <Button onClick={sync} disabled={syncing} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sync-btn">
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarSync className="w-4 h-4 mr-2" />}
            Sync hello@ calendar
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-blue-500" /></div>
            ) : events.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                <CalendarSync className="w-10 h-10 mx-auto mb-2" />
                <p>No events need linking. Click "Sync" to fetch from Outlook.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Subject</th>
                      <th className="px-3 py-2">Location</th>
                      <th className="px-3 py-2">Body Preview</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id} className="border-b hover:bg-slate-50" data-testid={`unlinked-row-${e.id}`}>
                        <td className="px-3 py-2 text-sm whitespace-nowrap">
                          <div>{e.scheduled_date}</div>
                          <div className="text-xs text-slate-400">{e.start_time}–{e.end_time}</div>
                        </td>
                        <td className="px-3 py-2 text-sm font-medium">{e.subject}</td>
                        <td className="px-3 py-2 text-sm">{e.location || '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-600 max-w-[400px] truncate">{e.body_preview || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            {e.raw?.webLink && (
                              <a href={e.raw.webLink} target="_blank" rel="noreferrer">
                                <Button size="icon" variant="ghost" className="h-8 w-8"><ExternalLink className="w-4 h-4" /></Button>
                              </a>
                            )}
                            <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, event: e, customerId: '' })} data-testid={`link-event-${e.id}`}>
                              <LinkIcon className="w-3 h-3 mr-1" /> Link
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => dismiss(e)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialog.open} onOpenChange={(open) => setDialog({ ...dialog, open })}>
        <DialogContent data-testid="link-event-dialog">
          <DialogHeader>
            <DialogTitle>Link Outlook event to customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500"><b>Subject:</b> {dialog.event?.subject}</p>
            <p className="text-sm text-slate-500"><b>Location:</b> {dialog.event?.location || '—'}</p>
            <Select value={dialog.customerId} onValueChange={(v) => setDialog({ ...dialog, customerId: v })}>
              <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.postcode}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, event: null, customerId: '' })}>Cancel</Button>
            <Button onClick={link} disabled={!dialog.customerId} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-link-event-btn">
              Link & Create Installation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default NeedsLinking;
